'use client'

import React from 'react'

import {
  ChevronDown,
  Heading1,
  Heading2,
  Heading3,
  Heading4
} from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'

import { useMediaQuery } from '@/hooks/use-media-query'

import { cn } from '@/utils'

import { MobileToolbarGroup, MobileToolbarItem } from './mobile-toolbar-group'
import { useToolbar } from './toolbar-provider'

const levels = [1, 2, 3, 4] as const
const headingIcons = {
  1: <Heading1 className="h-4 w-4" />,
  2: <Heading2 className="h-4 w-4" />,
  3: <Heading3 className="h-4 w-4" />,
  4: <Heading4 className="h-4 w-4" />
} as const

export const HeadingsToolbar = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  const { editor } = useToolbar()
  const t = useTranslations('components.tiptap.toolbars.headings')
  const isMobile = useMediaQuery('(max-width: 640px)')
  const activeLevel = levels.find((level) =>
    editor?.isActive('heading', { level })
  )
  const paragraphDirection = editor?.getAttributes('paragraph')?.dir as
    | 'auto'
    | 'ltr'
    | 'rtl'
    | undefined
  const headingDirection = editor?.getAttributes('heading')?.dir as
    | 'auto'
    | 'ltr'
    | 'rtl'
    | undefined
  const currentDirection = headingDirection ?? paragraphDirection

  const setParagraphPreservingDirection = () => {
    if (!editor) {
      return
    }

    const chain = editor.chain().focus().setParagraph()

    if (currentDirection) {
      chain.updateAttributes('paragraph', { dir: currentDirection })
    }

    chain.run()
  }

  const toggleHeadingPreservingDirection = (level: (typeof levels)[number]) => {
    if (!editor) {
      return
    }

    const chain = editor.chain().focus().toggleHeading({ level })

    if (currentDirection) {
      chain.updateAttributes('heading', { dir: currentDirection })
    }

    chain.run()
  }

  if (isMobile) {
    return (
      <MobileToolbarGroup label={activeLevel ? `H${activeLevel}` : t('normal')}>
        <MobileToolbarItem
          active={!editor?.isActive('heading')}
          onClick={setParagraphPreservingDirection}
        >
          {t('normal')}
        </MobileToolbarItem>
        {levels.map((level) => (
          <MobileToolbarItem
            active={editor?.isActive('heading', { level })}
            key={level}
            onClick={() => toggleHeadingPreservingDirection(level)}
          >
            <span className="mr-2">{headingIcons[level]}</span>
            {`H${level}`}
          </MobileToolbarItem>
        ))}
      </MobileToolbarGroup>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className={cn(
                'h-8 w-max gap-1 px-3 font-normal',
                editor?.isActive('heading') && 'bg-accent',
                className
              )}
              ref={ref}
              size="sm"
              type="button"
              variant="ghost"
              {...props}
            >
              {activeLevel ? headingIcons[activeLevel] : t('normal')}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              className={cn(
                'flex h-fit items-center gap-2',
                !editor?.isActive('heading') && 'bg-accent'
              )}
              onClick={setParagraphPreservingDirection}
            >
              {t('normal')}
            </DropdownMenuItem>
            {levels.map((level) => (
              <DropdownMenuItem
                className={cn(
                  'flex items-center gap-2',
                  editor?.isActive('heading', { level }) && 'bg-accent'
                )}
                key={level}
                onClick={() => toggleHeadingPreservingDirection(level)}
              >
                {`H${level}`}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TooltipTrigger>
      <TooltipContent>
        <span>{t('title')}</span>
      </TooltipContent>
    </Tooltip>
  )
})

HeadingsToolbar.displayName = 'HeadingsToolbar'
